import React, { createContext, useContext, useReducer } from 'react';

// Initial state
const initialState = {
  currentGame: null,
  games: [],
  series: [],
  matches: [],
  players: [],
  teams: [],
  venues: [],
  owners: [],
  contests: [],
  selectedSeries: null,
  gameStats: null,
  loading: false,
  error: null,
};

// Action types
const GAME_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_CURRENT_GAME: 'SET_CURRENT_GAME',
  SET_GAMES: 'SET_GAMES',
  ADD_GAME: 'ADD_GAME',
  UPDATE_GAME: 'UPDATE_GAME',
  REMOVE_GAME: 'REMOVE_GAME',
  SET_SERIES: 'SET_SERIES',
  SET_SELECTED_SERIES: 'SET_SELECTED_SERIES',
  SET_MATCHES: 'SET_MATCHES',
  SET_PLAYERS: 'SET_PLAYERS',
  SET_TEAMS: 'SET_TEAMS',
  SET_VENUES: 'SET_VENUES',
  SET_OWNERS: 'SET_OWNERS',
  SET_CONTESTS: 'SET_CONTESTS',
  ADD_CONTEST: 'ADD_CONTEST',
  UPDATE_CONTEST: 'UPDATE_CONTEST',
  REMOVE_CONTEST: 'REMOVE_CONTEST',
  SET_GAME_STATS: 'SET_GAME_STATS',
};

// Reducer
const gameReducer = (state, action) => {
  switch (action.type) {
    case GAME_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case GAME_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case GAME_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    case GAME_ACTIONS.SET_CURRENT_GAME:
      return {
        ...state,
        currentGame: action.payload,
      };
    case GAME_ACTIONS.SET_GAMES:
      return {
        ...state,
        games: Array.isArray(action.payload) ? action.payload : [],
        loading: false,
      };
    case GAME_ACTIONS.ADD_GAME:
      return {
        ...state,
        games: [...state.games, action.payload],
      };
    case GAME_ACTIONS.UPDATE_GAME:
      return {
        ...state,
        games: state.games.map(game =>
          game._id === action.payload._id ? action.payload : game
        ),
        currentGame: state.currentGame?._id === action.payload._id ? action.payload : state.currentGame,
      };
    case GAME_ACTIONS.REMOVE_GAME:
      return {
        ...state,
        games: state.games.filter(game => game._id !== action.payload),
        currentGame: state.currentGame?._id === action.payload ? null : state.currentGame,
      };
    case GAME_ACTIONS.SET_SERIES:
      return {
        ...state,
        series: action.payload,
        loading: false,
      };
    case GAME_ACTIONS.SET_SELECTED_SERIES:
      return {
        ...state,
        selectedSeries: action.payload,
      };
    case GAME_ACTIONS.SET_MATCHES:
      return {
        ...state,
        matches: action.payload,
        loading: false,
      };
    case GAME_ACTIONS.SET_PLAYERS:
      return {
        ...state,
        players: action.payload,
        loading: false,
      };
    case GAME_ACTIONS.SET_TEAMS:
      return {
        ...state,
        teams: action.payload,
        loading: false,
      };
    case GAME_ACTIONS.SET_VENUES:
      return {
        ...state,
        venues: action.payload,
        loading: false,
      };
    case GAME_ACTIONS.SET_OWNERS:
      return {
        ...state,
        owners: action.payload,
        loading: false,
      };
    case GAME_ACTIONS.SET_CONTESTS:
      return {
        ...state,
        contests: action.payload,
        loading: false,
      };
    case GAME_ACTIONS.ADD_CONTEST:
      return {
        ...state,
        contests: [...state.contests, action.payload],
      };
    case GAME_ACTIONS.UPDATE_CONTEST:
      return {
        ...state,
        contests: state.contests.map(contest =>
          contest._id === action.payload._id ? action.payload : contest
        ),
      };
    case GAME_ACTIONS.REMOVE_CONTEST:
      return {
        ...state,
        contests: state.contests.filter(contest => contest._id !== action.payload),
      };
    case GAME_ACTIONS.SET_GAME_STATS:
      return {
        ...state,
        gameStats: action.payload,
      };
    default:
      return state;
  }
};

// Create context
const GameContext = createContext();

// Game provider component
export const GameProvider = ({ children }) => {
  const [state, dispatch] = useReducer(gameReducer, initialState);

  // Set loading state
  const setLoading = (loading) => {
    dispatch({ type: GAME_ACTIONS.SET_LOADING, payload: loading });
  };

  // Set error
  const setError = (error) => {
    dispatch({ type: GAME_ACTIONS.SET_ERROR, payload: error });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: GAME_ACTIONS.CLEAR_ERROR });
  };

  // Set current game
  const setCurrentGame = (game) => {
    dispatch({ type: GAME_ACTIONS.SET_CURRENT_GAME, payload: game });
  };

  // Set games
  const setGames = (games) => {
    dispatch({ type: GAME_ACTIONS.SET_GAMES, payload: games });
  };

  // Add game
  const addGame = (game) => {
    dispatch({ type: GAME_ACTIONS.ADD_GAME, payload: game });
  };

  // Update game
  const updateGame = (game) => {
    dispatch({ type: GAME_ACTIONS.UPDATE_GAME, payload: game });
  };

  // Remove game
  const removeGame = (gameId) => {
    dispatch({ type: GAME_ACTIONS.REMOVE_GAME, payload: gameId });
  };

  // Set series
  const setSeries = (series) => {
    dispatch({ type: GAME_ACTIONS.SET_SERIES, payload: series });
  };

  // Set selected series
  const setSelectedSeries = (series) => {
    dispatch({ type: GAME_ACTIONS.SET_SELECTED_SERIES, payload: series });
  };

  // Set matches
  const setMatches = (matches) => {
    dispatch({ type: GAME_ACTIONS.SET_MATCHES, payload: matches });
  };

  // Set players
  const setPlayers = (players) => {
    dispatch({ type: GAME_ACTIONS.SET_PLAYERS, payload: players });
  };

  // Set teams
  const setTeams = (teams) => {
    dispatch({ type: GAME_ACTIONS.SET_TEAMS, payload: teams });
  };

  // Set venues
  const setVenues = (venues) => {
    dispatch({ type: GAME_ACTIONS.SET_VENUES, payload: venues });
  };

  // Set owners
  const setOwners = (owners) => {
    dispatch({ type: GAME_ACTIONS.SET_OWNERS, payload: owners });
  };

  // Set contests
  const setContests = (contests) => {
    dispatch({ type: GAME_ACTIONS.SET_CONTESTS, payload: contests });
  };

  // Add contest
  const addContest = (contest) => {
    dispatch({ type: GAME_ACTIONS.ADD_CONTEST, payload: contest });
  };

  // Update contest
  const updateContest = (contest) => {
    dispatch({ type: GAME_ACTIONS.UPDATE_CONTEST, payload: contest });
  };

  // Remove contest
  const removeContest = (contestId) => {
    dispatch({ type: GAME_ACTIONS.REMOVE_CONTEST, payload: contestId });
  };

  // Set game stats
  const setGameStats = (stats) => {
    dispatch({ type: GAME_ACTIONS.SET_GAME_STATS, payload: stats });
  };

  const value = {
    ...state,
    setLoading,
    setError,
    clearError,
    setCurrentGame,
    setGames,
    addGame,
    updateGame,
    removeGame,
    setSeries,
    setSelectedSeries,
    setMatches,
    setPlayers,
    setTeams,
    setVenues,
    setOwners,
    setContests,
    addContest,
    updateContest,
    removeContest,
    setGameStats,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};

// Custom hook to use game context
export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};